import { constructValue } from '@adornis/baseql/entities/construct.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import type { Person } from '../../entities/person.js';
import { UserListAPI } from '../api/UserListAPI.js';
import { UserListSelectionInterface } from '../entities/UserListSelectionInterface.js';
import { UserListStandardFieldConfiguration, UserListStandardSorting } from './UserListStandardView.js';

export const PRESELECTED_FILTER_URL_PARAM = 'filter';

/**
 * Returns the url parameter for the preselected filter
 * @param filter
 */
export function getPreselectedFilterURIComponent(filter: AdornisFilter): string {
  return `${PRESELECTED_FILTER_URL_PARAM}=${JSON.stringify(filter)}`;
}

export const useUserListPreselectedFilter = (
  view: View<typeof Person> | null,
  setView: (view: View<typeof Person>) => void,
) => {
  /**
   * a possible preselected filter for the list
   */
  const [preSelectedFilter, setPreSelectedFilter] = useState<AdornisFilter | null>(null);
  const [preSelectedFilterPersonCount, setPreSelectedFilterPersonCount] = useState(0);

  const [manualSearch, setManualSearch] = useState('');

  /**
   * Initialize the preselected filter from the url
   */
  useEffect(() => {
    const filter = routingStateSignal.value.searchParams.get(PRESELECTED_FILTER_URL_PARAM);
    if (filter) {
      const filterParsed = constructValue(JSON.parse(filter)) as AdornisFilter;
      filterParsed.sortingFields = UserListStandardSorting.sortingFields;
      filterParsed.sortingDirections = UserListStandardSorting.sortingDirections;

      /**
       * Build the new view to use for the list
       */
      const newView =
        view ??
        new View({
          configuredColumns: UserListStandardFieldConfiguration,
        });
      newView.filter = filterParsed;
      setPreSelectedFilter(filterParsed);
      setView(newView);
    } else {
      setPreSelectedFilter(null);
    }
  }, [routingStateSignal.value]);

  /**
   * Update the person count when the preselected filter changes
   */
  useEffect(() => {
    if (!preSelectedFilter) return;

    UserListAPI.previewSelection(
      new UserListSelectionInterface({
        filter: preSelectedFilter,
      }),
    )().then(result => {
      setPreSelectedFilterPersonCount(result);
    });
  }, [preSelectedFilter]);

  /**
   * Enrich the given view with the preselected filter
   */
  useEffect(() => {
    if (preSelectedFilter) {
      const timeoutID = setTimeout(() => {
        if (!view?.filter) return;
        view.filter.search = manualSearch;
        setView(view);
      }, 200);
      return () => clearTimeout(timeoutID);
    }
  }, [manualSearch, preSelectedFilter]);

  return {
    preSelectedFilter,
    preSelectedFilterPersonCount,
    setManualSearch,
    manualSearch,
  };
};
